import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable, { applyPlugin } from "jspdf-autotable";
import { useCompany } from "../../context/CompanyContext";
import { showToast } from "../../utils/customToast";

import MonthlyLedgerCards from "./components/MonthlyLedgerCards";
import MonthlyLedgerFilters from "./components/MonthlyLedgerFilters";
import MonthlyLedgerTable from "./components/MonthlyLedgerTable";

import { formatINR } from "./data/dummyMonthlyLedgerData";
import "./MonthlyLedgerSummary.css";

try {
  applyPlugin(jsPDF);
} catch (e) {
  // autoTable already applied or fallback
}

export default function MonthlyLedgerSummary() {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedCompany } = useCompany();
  const companyId = selectedCompany?.id || localStorage.getItem("companyId") || "4211";
  const BASE_API_URL = process.env.REACT_APP_BASE_API_URL || "http://localhost:5000/api";

  const companyInfo = {
    name: selectedCompany?.name || selectedCompany?.Name || "DAZY TOURE TRAVEL'S",
    address: selectedCompany?.Address || selectedCompany?.address || "Sahakar Marg",
    gstin: selectedCompany?.GSTNO || selectedCompany?.gstin || "08AGCPS3005M1ZB",
    stateCode: selectedCompany?.StateCode || selectedCompany?.stateCode || "08",
  };

  const incomingAccountId = location.state?.accountId ? String(location.state.accountId) : "";

  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState(incomingAccountId);
  const [fromDate, setFromDate] = useState("2026-04");
  const [toDate, setToDate] = useState("2027-03");
  const [tableData, setTableData] = useState([]);
  const [openingBalance, setOpeningBalance] = useState(0);
  const [openingType, setOpeningType] = useState("Dr");
  const [accountInfo, setAccountInfo] = useState(null);
  const [loading, setLoading] = useState(false);

  // Sync selectedAccountId if navigated from Group Ledger Summary or another page
  useEffect(() => {
    if (location.state?.accountId) {
      setSelectedAccountId(String(location.state.accountId));
    }
  }, [location.state?.accountId]);

  const cleanAccName = (rawName) => {
    if (!rawName) return "";
    return String(rawName)
      .replace(/\s*\(\d+\)\s*$/, "")
      .replace(/\s*-\s*\d+$/, "")
      .trim();
  };

  // Load accounts list on mount / company change
  useEffect(() => {
    if (!companyId) return;
    const fetchAccounts = async () => {
      try {
        const res = await axios.get(`${BASE_API_URL}/reports/ledger/accounts/${companyId}`);
        if (res.data?.success && Array.isArray(res.data.data) && res.data.data.length > 0) {
          const mapped = res.data.data.map((item) => {
            const cName = cleanAccName(item.HEADName || item.HEAD_NAME || item.name);
            return {
              id: String(item.HEAD_CODE || item.id),
              code: String(item.HEAD_CODE || item.code),
              name: cName,
              label: cName,
              group: item.GroupName || item.PARENT_NAME || "General",
              type: "Asset",
              openingBalance: Number(item.OpeningBal || item.OPENING_BAL || 0),
              balanceType: item.OpenBlType || item.DR_CR || "Dr",
            };
          });
          setAccounts(mapped);
          if (incomingAccountId && mapped.some((a) => a.id === incomingAccountId || a.code === incomingAccountId)) {
            setSelectedAccountId(incomingAccountId);
          } else if (!selectedAccountId && mapped.length > 0) {
            setSelectedAccountId(mapped[0].id);
          }
        }
      } catch (err) {
        console.error("Failed to fetch accounts:", err);
        showToast.error("Failed to load ledger accounts");
      }
    };
    fetchAccounts();
  }, [BASE_API_URL, companyId, incomingAccountId]);

  // Fetch monthly summary data from API
  const fetchMonthlySummary = useCallback(async () => {
    if (!companyId || !selectedAccountId) return;
    setLoading(true);
    try {
      const fromDateStr = `${fromDate}-01`;
      const [toY, toM] = toDate.split("-");
      const lastDay = new Date(parseInt(toY, 10), parseInt(toM, 10), 0).getDate();
      const toDateStr = `${toDate}-${String(lastDay).padStart(2, "0")}`;

      const res = await axios.get(`${BASE_API_URL}/reports/ledger/monthly-summary/${companyId}`, {
        params: {
          headCode: selectedAccountId,
          fromDate: fromDateStr,
          toDate: toDateStr,
        },
      });

      if (res.data?.success && res.data.data) {
        const data = res.data.data;
        setTableData(data.months || []);
        setOpeningBalance(data.openingBalance?.amount || 0);
        setOpeningType(data.openingBalance?.type || "Dr");
        setAccountInfo(data.account || null);
      } else {
        setTableData([]);
        showToast.info("No data found for selected period");
      }
    } catch (err) {
      console.error("Failed to fetch monthly summary:", err);
      setTableData([]);
      showToast.error("Failed to load monthly summary data");
    } finally {
      setLoading(false);
    }
  }, [BASE_API_URL, companyId, selectedAccountId, fromDate, toDate]);

  useEffect(() => {
    if (selectedAccountId) {
      fetchMonthlySummary();
    }
  }, [selectedAccountId, fetchMonthlySummary]);

  const selectedAccount = useMemo(() => {
    return accounts.find((a) => (a.id || a.code) === selectedAccountId) || accounts[0] || null;
  }, [accounts, selectedAccountId]);

  const cleanAccountName = useMemo(() => {
    return cleanAccName(selectedAccount?.label || selectedAccount?.name || "");
  }, [selectedAccount]);

  const safeAccountName = useMemo(() => {
    return cleanAccountName
      .replace(/[^a-zA-Z0-9_-]/g, "_")
      .replace(/__+/g, "_")
      .replace(/^_|_$/g, "");
  }, [cleanAccountName]);

  // Calculate summary metrics
  const totals = useMemo(() => {
    let totDebit = 0;
    let totCredit = 0;
    tableData.forEach((row) => {
      totDebit += Number(row.debit || 0);
      totCredit += Number(row.credit || 0);
    });

    const opBalSigned = openingType === "Cr" ? -openingBalance : openingBalance;
    const closingVal = opBalSigned + totDebit - totCredit;
    const closingType = closingVal >= 0 ? "Dr" : "Cr";

    return {
      openingBalance: openingBalance,
      openingType: openingType,
      debit: Math.round(totDebit * 100) / 100,
      credit: Math.round(totCredit * 100) / 100,
      closingBalance: Math.round(Math.abs(closingVal) * 100) / 100,
      closingType: closingType,
    };
  }, [tableData, openingBalance, openingType]);

  const handleApplyFilter = () => {
    fetchMonthlySummary();
    showToast.info("Report updated for selected period");
  };

  const handleResetFilter = () => {
    setFromDate("2026-04");
    setToDate("2027-03");
    if (accounts.length > 0) {
      setSelectedAccountId(accounts[0].id);
    }
    showToast.success("Filters reset to default Financial Year");
  };

  const handleSelectMonth = (monthRow) => {
    navigate("/ledger-transactions", {
      state: {
        accountId: selectedAccountId,
        accountName: selectedAccount?.label || selectedAccount?.name,
        monthKey: monthRow.monthKey,
        monthLabel: monthRow.month,
        group: accountInfo?.groupName || selectedAccount?.group || "General",
        type: selectedAccount?.type || "Asset",
      },
    });
  };

  const fyLabel = useMemo(() => {
    const [fY] = fromDate.split("-");
    const [tY] = toDate.split("-");
    return `Financial Year ${fY}-${tY}`;
  }, [fromDate, toDate]);

  const asOnTime = useMemo(() => {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, "0");
    const mNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const mon = mNames[now.getMonth()];
    const year = now.getFullYear();
    const hours = now.getHours();
    const mins = String(now.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    const h12 = hours % 12 || 12;
    return `${day}-${mon}-${year} ${h12}:${mins} ${ampm}`;
  }, []);

  // Export to Excel - Exactly matching GST Tax Register formal structure
  const handleExportExcel = () => {
    try {
      const activeCompName = String(companyInfo.name).toUpperCase();
      const compDetails = `Address: ${companyInfo.address || ""}  |  GSTIN: ${companyInfo.gstin || ""}  |  State: ${companyInfo.stateCode || "08"}`;
      const reportTitle = "MONTHLY LEDGER SUMMARY";
      const periodSubtitle = `Account: ${cleanAccountName}  |  Period: ${fromDate} to ${toDate}  |  As on: ${asOnTime}`;

      const headers = ["Month", "Debit (INR)", "Credit (INR)", "Closing Balance (INR)"];

      const isOpDr = totals.openingType === "Dr" && totals.openingBalance > 0;
      const isOpCr = totals.openingType === "Cr" && totals.openingBalance > 0;

      const openingRow = [
        "Opening Balance",
        isOpDr ? totals.openingBalance : "-",
        isOpCr ? totals.openingBalance : "-",
        "-",
      ];

      const dataRows = tableData.map((r) => [
        r.month,
        r.debit > 0 ? r.debit : 0,
        r.credit > 0 ? r.credit : 0,
        `${formatINR(r.closingBalance)} ${r.balanceType || "Dr"}`,
      ]);

      const totalRow = [
        "Total",
        totals.debit,
        totals.credit,
        `${formatINR(totals.closingBalance)} ${totals.closingType}`,
      ];

      const wsData = [
        [activeCompName],
        [compDetails],
        [reportTitle],
        [periodSubtitle],
        [],
        headers,
        openingRow,
        ...dataRows,
        totalRow,
        [],
        ["etour - Design & Develop by Response Infoway"],
      ];

      const worksheet = XLSX.utils.aoa_to_sheet(wsData);
      worksheet["!merges"] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: 3 } },
        { s: { r: 1, c: 0 }, e: { r: 1, c: 3 } },
        { s: { r: 2, c: 0 }, e: { r: 2, c: 3 } },
        { s: { r: 3, c: 0 }, e: { r: 3, c: 3 } },
      ];

      worksheet["!cols"] = [
        { wch: 22 }, // Month
        { wch: 18 }, // Debit
        { wch: 18 }, // Credit
        { wch: 22 }, // Closing Balance
      ];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Monthly_Summary");
      const fileName = safeAccountName
        ? `Monthly_Ledger_Summary_${safeAccountName}.xlsx`
        : "Monthly_Ledger_Summary.xlsx";
      XLSX.writeFile(workbook, fileName);
      showToast.success("Excel exported successfully");
    } catch (err) {
      console.error(err);
      showToast.error("Failed to export Excel");
    }
  };

  // Export to PDF - Exactly matching GST Tax Register formal structure
  const handleExportPDF = () => {
    try {
      const doc = new jsPDF("p", "mm", "a4");
      const pageWidth = doc.internal.pageSize.getWidth();
      const activeCompName = String(companyInfo.name).toUpperCase();
      const compLine2 = [companyInfo.address, companyInfo.gstin ? `GSTIN: ${companyInfo.gstin}` : "", companyInfo.stateCode ? `State Code: ${companyInfo.stateCode}` : ""].filter(Boolean).join("   |   ");
      const reportTitle = "MONTHLY LEDGER SUMMARY";

      // 1. Company Name Header (Centered, Bold, 15pt)
      doc.setFont("helvetica", "bold");
      doc.setFontSize(15);
      doc.setTextColor(0, 0, 0);
      doc.text(activeCompName, pageWidth / 2, 14, { align: "center" });

      // 2. Company Details
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
      doc.setTextColor(40, 40, 40);
      doc.text(compLine2, pageWidth / 2, 19, { align: "center" });

      // 3. Report Title Underlined
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10.5);
      doc.setTextColor(0, 0, 0);
      doc.text(reportTitle, pageWidth / 2, 26, { align: "center" });
      const titleWidth = doc.getTextWidth(reportTitle);
      doc.setLineWidth(0.75);
      doc.setDrawColor(0, 0, 0);
      doc.line(pageWidth / 2 - titleWidth / 2, 27.5, pageWidth / 2 + titleWidth / 2, 27.5);

      // 4. Divider Line
      doc.setLineWidth(1);
      doc.setDrawColor(0, 0, 0);
      doc.line(14, 31, pageWidth - 14, 31);

      // 5. Meta Information Bar
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8.5);
      doc.setTextColor(0, 0, 0);
      doc.text(`Account: ${cleanAccountName}`, 14, 36);
      doc.text(`Period: ${fromDate} to ${toDate}   |   Date: ${asOnTime}`, pageWidth - 14, 36, { align: "right" });

      // 6. Divider Line below Meta bar
      doc.setLineWidth(0.5);
      doc.setDrawColor(0, 0, 0);
      doc.line(14, 39, pageWidth - 14, 39);

      const isOpDr = totals.openingType === "Dr" && totals.openingBalance > 0;
      const isOpCr = totals.openingType === "Cr" && totals.openingBalance > 0;

      const tableRows = [
        [
          "Opening Balance",
          isOpDr ? formatINR(totals.openingBalance) : "-",
          isOpCr ? formatINR(totals.openingBalance) : "-",
          "-",
        ],
        ...tableData.map((r) => [
          r.month,
          formatINR(r.debit),
          formatINR(r.credit),
          `${formatINR(r.closingBalance)} ${r.balanceType || "Dr"}`,
        ]),
        [
          "Total",
          formatINR(totals.debit),
          formatINR(totals.credit),
          `${formatINR(totals.closingBalance)} ${totals.closingType}`,
        ],
      ];

      autoTable(doc, {
        head: [["Month", "Debit (INR)", "Credit (INR)", "Closing Balance (INR)"]],
        body: tableRows,
        startY: 42,
        theme: "striped",
        headStyles: {
          fillColor: [6, 78, 72], // --primary-teal
          textColor: 255,
          fontStyle: "bold",
          fontSize: 8.5,
          halign: "center",
        },
        styles: {
          fontSize: 8,
          cellPadding: 2.5,
          textColor: 30,
        },
        columnStyles: {
          0: { halign: "left" },
          1: { halign: "right" },
          2: { halign: "right" },
          3: { halign: "right" },
        },
        didDrawPage: (data) => {
          const pageCount = doc.internal.getNumberOfPages();
          doc.setFontSize(8);
          doc.setTextColor(120, 120, 120);
          doc.text("etour - Design & Develop by Response Infoway", 14, doc.internal.pageSize.getHeight() - 8);
          doc.text(`Page ${data.pageNumber} of ${pageCount}`, pageWidth - 14, doc.internal.pageSize.getHeight() - 8, { align: "right" });
        },
      });

      const fileName = safeAccountName
        ? `Monthly_Ledger_Summary_${safeAccountName}.pdf`
        : "Monthly_Ledger_Summary.pdf";
      doc.save(fileName);
      showToast.success("PDF exported successfully");
    } catch (err) {
      console.error(err);
      showToast.error("Failed to export PDF");
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="mls_page_wrapper">
      {/* Formal Printable Header (Visible during Print only) */}
      <div className="mls_print_header d-none d-print-block text-center mb-3">
        <h2 className="mb-0 fw-bold text-uppercase" style={{ fontSize: "18px", color: "#000000", letterSpacing: "0.5px" }}>
          {companyInfo.name}
        </h2>
        <div style={{ fontSize: "11px", color: "#333333", marginTop: "2px" }}>
          {[companyInfo.address, companyInfo.gstin ? `GSTIN: ${companyInfo.gstin}` : "", companyInfo.stateCode ? `State Code: ${companyInfo.stateCode}` : ""].filter(Boolean).join("   |   ")}
        </div>
        <div className="fw-bold mt-2" style={{ fontSize: "13px", color: "#000000" }}>
          MONTHLY LEDGER SUMMARY
        </div>
        <div style={{ fontSize: "10.5px", color: "#555555" }}>
          Account: {cleanAccountName} | Period: {fromDate} to {toDate} | As on: {asOnTime}
        </div>
      </div>

      {/* Top Header & Breadcrumb */}
      <div className="mls_header_section">
        <div className="mls_breadcrumb_bar">
          Reports <span>&gt;</span> <strong>Monthly Ledger Summary</strong>
        </div>
        <div className="mls_as_on_badge">
          <i className="fa-regular fa-clock"></i>
          As on: {asOnTime}
        </div>
      </div>

      {/* Filter Card */}
      <MonthlyLedgerFilters
        accounts={accounts}
        selectedAccountId={selectedAccountId}
        onChangeAccount={setSelectedAccountId}
        fromDate={fromDate}
        toDate={toDate}
        onChangeFromDate={setFromDate}
        onChangeToDate={setToDate}
        onApplyFilter={handleApplyFilter}
        onResetFilter={handleResetFilter}
      />

      {/* KPI Cards Row */}
      <MonthlyLedgerCards
        openingBalance={totals.openingBalance}
        openingType={totals.openingType}
        totalDebit={totals.debit}
        totalCredit={totals.credit}
        closingBalance={totals.closingBalance}
        closingType={totals.closingType}
      />

      {/* Main Table */}
      <MonthlyLedgerTable
        data={tableData}
        totals={totals}
        openingBalance={totals.openingBalance}
        openingType={totals.openingType}
        onSelectMonth={handleSelectMonth}
        onExportExcel={handleExportExcel}
        onExportPDF={handleExportPDF}
        onPrint={handlePrint}
        loading={loading}
      />

      {/* DEDICATED PRINT CONTAINER (Hidden on screen, Active during @media print) */}
      <div className="mls_print_area d-none">
        {/* Company Header */}
        <div style={{ textAlign: "center", borderBottom: "2px solid #000000", paddingBottom: "8px", marginBottom: "10px" }}>
          <h2 style={{ margin: "0 0 3px 0", fontSize: "19px", fontWeight: "bold", textTransform: "uppercase", color: "#000000" }}>
            {companyInfo.name}
          </h2>
          <div style={{ fontSize: "11px", color: "#222222", lineHeight: 1.4 }}>
            {companyInfo.address && `${companyInfo.address}  |  `}
            <strong>GSTIN:</strong> {companyInfo.gstin}
            {companyInfo.stateCode && `  |  State Code: ${companyInfo.stateCode}`}
          </div>
          <div style={{ marginTop: "6px", fontSize: "13px", fontWeight: "bold", color: "#000000" }}>
            MONTHLY LEDGER SUMMARY
          </div>
        </div>

        {/* Print Filter Meta */}
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: "11px", marginBottom: "8px", borderBottom: "1px solid #000000", paddingBottom: "4px" }}>
          <div>
            <strong>Account:</strong> {cleanAccountName}  |  <strong>Period:</strong> {fromDate} to {toDate}
          </div>
          <div style={{ textAlign: "right" }}>
            <strong>Date:</strong> {asOnTime}
          </div>
        </div>

        {/* Table */}
        <table className="mls_print_table">
          <thead>
            <tr>
              <th style={{ width: "28%" }}>Month</th>
              <th style={{ width: "24%", textAlign: "right" }}>Debit (₹)</th>
              <th style={{ width: "24%", textAlign: "right" }}>Credit (₹)</th>
              <th style={{ width: "24%", textAlign: "right" }}>Closing Balance (₹)</th>
            </tr>
          </thead>
          <tbody>
            <tr className="print_opening_row">
              <td style={{ fontWeight: 700 }}>Opening Balance</td>
              <td style={{ textAlign: "right", fontWeight: 600 }}>
                {totals.openingType === "Dr" && totals.openingBalance > 0 ? formatINR(totals.openingBalance) : "-"}
              </td>
              <td style={{ textAlign: "right", fontWeight: 600 }}>
                {totals.openingType === "Cr" && totals.openingBalance > 0 ? formatINR(totals.openingBalance) : "-"}
              </td>
              <td style={{ textAlign: "right" }}>-</td>
            </tr>
            {tableData.map((r, idx) => (
              <tr key={r.monthKey || idx} className={idx % 2 === 1 ? "print_row_alt" : ""}>
                <td>{r.month}</td>
                <td style={{ textAlign: "right" }}>{r.debit > 0 ? formatINR(r.debit) : "-"}</td>
                <td style={{ textAlign: "right" }}>{r.credit > 0 ? formatINR(r.credit) : "-"}</td>
                <td style={{ textAlign: "right", fontWeight: 600 }}>
                  {formatINR(r.closingBalance)} {r.balanceType || "Dr"}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="print_total_row">
              <td style={{ fontWeight: "bold" }}>Total</td>
              <td style={{ textAlign: "right", fontWeight: "bold" }}>{formatINR(totals.debit)}</td>
              <td style={{ textAlign: "right", fontWeight: "bold" }}>{formatINR(totals.credit)}</td>
              <td style={{ textAlign: "right", fontWeight: "bold" }}>
                {formatINR(totals.closingBalance)} {totals.closingType || "Dr"}
              </td>
            </tr>
          </tfoot>
        </table>

        {/* Footer */}
        <div className="mls_print_footer">
          <span>etour - Design & Develop by Response Infoway</span>
          <span>Printed on: {asOnTime}</span>
        </div>
      </div>
    </div>
  );
}
